import { FC, useEffect, useState } from "react";
import { Button, Form, ProgressBar, Spinner } from "react-bootstrap";
import { formatModuleName } from "../_helpers/utils";
import useAPI from "../_hooks/useAPI";
import { OptionTypes } from "../_models/types";
import { quizService } from "../_services";
import { Rating } from "react-simple-star-rating";
import AlertWrapper from "../_helpers/alert-wrapper";

interface IProps {
    moduleNumber: number;
    moduleName: string;
    quizResult: () => void;
}

const AssessmentComponent: FC<IProps> = ({
    moduleNumber,
    moduleName,
    quizResult: refreshQuizResult,
}) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [quizProgress, setQuizProgress] = useState<{ progress: number; result: number }>();
    const [moduleQuiz, setModuleQuiz] = useState<{ questions: any }>();
    const [loadedQuiz, setLoadedQuiz] = useState(-1);
    const [Loading, setLoading] = useState(false);
    const [RatedCourse, setRatedCourse] = useState(false);
    const [selectedAnswer, setSeletedAnswer] = useState(-1);
    const [activeOption, setActiveOption] = useState(-1);
    const [quizResult, setQuizResult] = useState<{ progress: number; result: number } | null>(null);
    const [Resetting, setResetting] = useState(false);
    const fetchWrapper = useAPI();

    // Fetch quiz data and restore progress
    useEffect(() => {
        const getQuizStatus = async () => {
            const status = await quizService.getQuizStatus(fetchWrapper, moduleName);
            setQuizProgress(status);
        };

        const getQuizByModule = async () => {
            const data = await quizService.getQuizByModule(fetchWrapper, moduleName);
            setModuleQuiz(data);

            if (data?.questions?.length) {
                const firstUnansweredIndex = data.questions.findIndex((q: any) => !q.answered);
                const startIndex =
                    firstUnansweredIndex !== -1 ? firstUnansweredIndex : data.questions.length;
                setLoadedQuiz(startIndex);

                if (firstUnansweredIndex !== -1) {
                    const q = data.questions[firstUnansweredIndex];
                    if (q?.chosen_option_id) setActiveOption(q.chosen_option_id);
                }
            }
        };

        getQuizStatus();
        getQuizByModule();
    }, []);

    useEffect(() => {
        if (
            !Loading &&
            moduleQuiz?.questions?.length &&
            loadedQuiz >= moduleQuiz.questions.length
        ) {
            getQuizResult();
        }
    }, [Loading, loadedQuiz, moduleQuiz]);

    const getQuizResult = async () => {
        setQuizResult(await quizService.getQuizResultMapped(fetchWrapper, moduleName));
    };

    const getProgressBar = (value: number) => {
        return <ProgressBar now={value} label={`${value}%`} />;
    };

    const submitAnswer = async () => {
        setLoading(true);
        if (selectedAnswer !== -1) {
            try {
                await quizService.answerQuestion(
                    fetchWrapper,
                    { option: Object.values(OptionTypes)[selectedAnswer] },
                    moduleName,
                    loadedQuiz + 1
                );
                refreshQuizResult();
                setLoadedQuiz(loadedQuiz + 1);
                setSeletedAnswer(-1);
                setActiveOption(-1);
            } catch (error) {
                console.error("Error submitting answer:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    const selectAnswer = (e: any, optionId: number) => {
        setActiveOption(optionId);
        setSeletedAnswer(Number(e.target.value));
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const restart = () => {
        setLoadedQuiz(0);
        setQuizResult(null);
    };

    // Reset Quiz Feature
    const resetQuiz = async () => {
        setResetting(true);
        try {
            await quizService.resetQuizProgress(fetchWrapper, moduleName);
            const data = await quizService.getQuizByModule(fetchWrapper, moduleName);
            setModuleQuiz(data);
            setLoadedQuiz(0);
            setQuizResult(null);
            setActiveOption(-1);
            setSeletedAnswer(-1);
            //  Optional: notify parent if needed (to hide cert tab)
            if (refreshQuizResult) refreshQuizResult();
        } catch (error) {
            console.error("Error resetting quiz:", error);
        } finally {
            setResetting(false);
        }
    };

    const rateCourse = async (rate: number) => {
        try {
            await quizService.rateQuiz(fetchWrapper, { rating: rate }, moduleName);
            setRatedCourse(true);
        } catch (error) {
            console.error("Error rating course:", error);
        }
    };

    return (
        <>
            {moduleQuiz && loadedQuiz !== -1 ? (
                <div className="col-12 assessment">
                    {moduleQuiz.questions[loadedQuiz] ? (
                        <div className="w-100 py-3">
                            {/*  Improved header area */}
                            <div
                                className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-4"
                                style={{ gap: "1rem" }}
                            >
                                <div>
                                    <h2
                                        className="fw-bold text-danger mb-0"
                                        style={{ fontSize: "1.8rem", lineHeight: "1.2" }}
                                    >
                                        {formatModuleName(moduleName)}
                                    </h2>
                                    <h5
                                        className="fw-semibold text-secondary mb-0"
                                        style={{ fontSize: "1.1rem" }}
                                    >
                                        Assessment
                                    </h5>
                                </div>

                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={resetQuiz}
                                    disabled={Resetting}
                                    className="rounded-pill px-3 py-1"
                                    style={{
                                        fontWeight: 500,
                                        borderWidth: "1.5px",
                                        color: "#b00020",
                                        borderColor: "#b00020",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {Resetting ? "Resetting..." : "↻ Reset Quiz"}
                                </Button>
                            </div>

                            {/* Question area */}
                            <h4 className="mt-3 fw-bold text-secondary">
                                Question {loadedQuiz + 1} of {moduleQuiz.questions.length}
                            </h4>
                            <p className="assessment__instructions text-muted mb-3">
                                Choose the correct answer to the following statement by selecting it
                                and clicking on the submit button.
                            </p>
                            <h3
                                className="assessment__question text-danger"
                                style={{ fontWeight: "600" }}
                            >
                                {`${moduleQuiz.questions[loadedQuiz].text}`}
                            </h3>

                            <div className="w-100 mb-3">
                                {moduleQuiz.questions[loadedQuiz].options.map(
                                    (opt: any, i: number) => (
                                        <Form.Check
                                            key={i}
                                            checked={
                                                activeOption === opt.id ||
                                                opt.id ===
                                                moduleQuiz.questions[loadedQuiz]
                                                    ?.chosen_option_id
                                            }
                                            inline
                                            label={opt.text}
                                            type="radio"
                                            id={opt.id}
                                            value={i}
                                            name="group1"
                                            className="d-block assessment__answer my-2"
                                            onChange={(e) => selectAnswer(e, opt.id)}
                                        />
                                    )
                                )}
                            </div>

                            <div className="w-100 mt-4">
                                <Button
                                    variant="primary"
                                    size="lg"
                                    onClick={submitAnswer}
                                    disabled={Loading || selectedAnswer === -1}
                                    className="px-5"
                                >
                                    {Loading ? "Submitting..." : "Submit"}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="w-100 assessment__quiz-result">
                            <div className="assessment__quiz-result__head">
                                <p className="assessment__header-text-progress">
                                    Your final score is
                                </p>
                                {/* FIXED: prevent undefined% */}
                                <p className="assessment__score">
                                    {quizResult ? `${quizResult.result}%` : "0%"}
                                </p>
                            </div>
                            {/* FIXED: safe progress bar rendering */}
                            {getProgressBar(quizResult?.result ?? 0)}

                            {(quizResult?.result as number) < 80 ? (
                                <p className="assessment__instructions mt-2">
                                    The pass mark is 80%. Click{" "}
                                    <span
                                        className="link"
                                        style={{ cursor: "pointer", color: "#b00020", fontWeight: 600 }}
                                        onClick={resetQuiz}
                                    >
                                        Here
                                    </span>{" "}
                                    to try the assessment again.
                                </p>
                            ) : (
                                <div className="assessment__quiz-result__rate text-center mt-4">
                                    <hr />
                                    {RatedCourse && (
                                        <AlertWrapper
                                            variant="success"
                                            message="Successfully rated course."
                                            closeMsg={() => setRatedCourse(false)}
                                        />
                                    )}

                                    <h3>Rate The Course</h3>
                                    <Rating onClick={rateCourse} fillColor="#fa551e" />

                                    {/* Add Redo button */}
                                    <div className="mt-4">
                                        <p className="text-muted mb-2">Want to try the assessment again?</p>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={resetQuiz}
                                            disabled={Resetting}
                                            className="rounded-pill px-3 py-1"
                                            style={{
                                                fontWeight: 500,
                                                borderWidth: "1.5px",
                                                color: "#b00020",
                                                borderColor: "#b00020",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {Resetting ? "Resetting..." : "↻ Redo Quiz"}
                                        </Button>
                                    </div>
                                </div>

                            )}
                        </div>
                    )}
                </div>
            ) : (
                <Spinner animation="border" size="sm" />
            )}
        </>
    );
};

export default AssessmentComponent;

// Add this at the bottom of your file, after export default AssessmentComponent
const styles = `
/* -------------------- RESET BUTTON (unchanged, as you like it) -------------------- */
.assessment button.btn-outline-danger {
  border: 2px solid #b00020 !important;
  color: #b00020 !important;
  font-weight: 600 !important;
  font-size: 0.8rem !important;
  border-radius: 8px !important;
  padding: 5px 12px !important;
  background-color: transparent !important;
  transition: all 0.25s ease-in-out !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 5px !important;
  height: 32px !important;
  min-width: 90px !important;
}

.assessment button.btn-outline-danger:hover {
  background: linear-gradient(145deg, #b00020, #9a001b) !important;
  color: #fff !important;
  border-color: #b00020 !important;
  box-shadow: 0 3px 8px rgba(176, 0, 32, 0.25) !important;
  transform: translateY(-1px) !important;
}

/* -------------------- SUBMIT BUTTON -------------------- */
.assessment button.btn-primary {
  background: linear-gradient(135deg, #b00020, #8e0018) !important;
  border: none !important;
  border-radius: 12px !important;
  color: #fff !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  padding: 11px 28px !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 4px 12px rgba(176, 0, 32, 0.25) !important;
  transition: all 0.25s ease-in-out !important;
}

.assessment button.btn-primary:hover {
  background: linear-gradient(135deg, #c00022, #a0001d) !important;
  transform: translateY(-2px) scale(1.03) !important;
  box-shadow: 0 6px 14px rgba(176, 0, 32, 0.35) !important;
}

.assessment button.btn-primary:active {
  transform: translateY(1px) scale(0.98) !important;
  background: linear-gradient(135deg, #900018, #700012) !important;
  box-shadow: 0 2px 8px rgba(176, 0, 32, 0.2) !important;
}

/* -------------------- RESPONSIVE -------------------- */

/*  Tablet and smaller */
@media (max-width: 768px) {
  .assessment button.btn-primary {
    width: 100% !important;
    font-size: 0.9rem !important;
    padding: 10px 0 !important;
    border-radius: 8px !important;
    box-shadow: 0 2px 8px rgba(176, 0, 32, 0.2) !important;
    display: block !important;
    margin-top: 10px !important;
  }
}
.assessment button.btn-outline-danger {
  font-family: 'Roboto', 'Segoe UI', 'Open Sans', sans-serif !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
}
/*  Mobile (≤576px) */
@media (max-width: 576px) {
  .assessment button.btn-primary {
    width: 100% !important;
    font-size: 0.9rem !important;
    padding: 10px 0 !important;
    border-radius: 8px !important;
  }
}
`;

if (typeof document !== "undefined" && !document.getElementById("assessment-final-styles")) {
    const styleEl = document.createElement("style");
    styleEl.id = "assessment-final-styles";
    styleEl.innerHTML = styles;
    document.head.appendChild(styleEl);
}
